import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DropdownProps } from '../../../types/DesignSystem/Dropdown';
import FieldTitle from '../FieldTitle/FieldTitle';
import { MenuDownIcon } from '../../../assests/GmsIcons';
import Typography from '../Typography/Typography';
import OverlayIcon from '../../GMS/Rules/Components/OverlayIcon';

const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  placeholder = 'Select an option',
  additionalInfo = '',
  infoMessage = '',
  className = '',
  usePortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const calculatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      return {
        position: 'fixed' as const,
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      };
    }
    return null;
  }, []);

  const handleToggle = () => {
    if (disabled) return;

    if (!isOpen && usePortal) {
      const position = calculatePosition();
      setMenuStyle(position);
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsidePortalMenu = usePortal && (target as Element).closest?.('[data-dropdown-portal]');

      if (!isInsideContainer && !isInsidePortalMenu) {
        if (isOpen) {
          setIsOpen(false);
          onBlur?.();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onBlur, usePortal]);

  const menuContent = (
    <div
      data-dropdown-portal={usePortal ? 'true' : undefined}
      style={usePortal ? menuStyle ?? undefined : undefined}
      className={`tw-bg-white tw-border tw-border-grey-200 tw-rounded-1 tw-shadow-md tw-max-h-64 tw-overflow-y-auto ${usePortal ? '' : 'tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-z-10'
        }`}
    >
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => {
            onChange?.(option.value);
            setIsOpen(false);
            onBlur?.();
          }}
          className={`tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-body-primary tw-transition-colors hover:tw-bg-blue-100
            ${value === option.value
              ? 'tw-bg-blue-100 tw-text-grey-900 tw-font-medium'
              : 'tw-text-grey-700 hover:tw-text-grey-900'
            }
          `}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const shouldShowMenu = isOpen && !disabled && (!usePortal || menuStyle !== null);

  return (
    <div ref={containerRef} className={className}>
      {label !== '' && (
        <div className="tw-flex tw-items-center tw-gap-2">
          <FieldTitle label={label} required={required} className="tw-mb-2" />
          {infoMessage && (
            <div className="tw-mb-2">
              <OverlayIcon message={infoMessage} placement="top" />
            </div>
          )}
        </div>
      )}

      <div className="tw-relative">
        <button
          ref={buttonRef}
          onClick={handleToggle}
          onBlur={(e) => {
            if (!containerRef.current?.contains(e.relatedTarget as Node)) {
              onBlur?.();
            }
          }}
          disabled={disabled}
          className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-border-grey-200 tw-rounded-1 tw-text-body-primary tw-text-left tw-flex tw-items-center tw-justify-between tw-transition-colors
            ${disabled
              ? 'tw-bg-grey-100 tw-text-grey-500 tw-cursor-not-allowed'
              : 'tw-bg-white hover:tw-border-grey-300 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500'
            }
            ${error ? 'tw-border-red-error-900 focus:tw-ring-red-error-900' : ''}
          `}
        >
          <span className={disabled ? 'tw-text-grey-500' : selectedOption ? 'tw-text-grey-900' : 'tw-text-grey-500'}>
            {selectedOption?.label || placeholder}
          </span>
          <MenuDownIcon />
        </button>

        {shouldShowMenu && (
          usePortal ? createPortal(menuContent, document.body) : menuContent
        )}
      </div>

      {helperText && (
        <Typography
          variant="caption-metadata"
          className={`tw-mt-1 ${error ? 'tw-text-red-error-900' : 'tw-text-grey-600'}`}
        >
          {helperText}
        </Typography>
      )}

      {additionalInfo && (
        <Typography
          variant="caption-metadata"
          className="tw-mt-2 tw-text-grey-600"
        >
          {additionalInfo}
        </Typography>
      )}
    </div>
  );
};

export default Dropdown;
